// Lib
import React, { ReactElement } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Components
import ElementExportPopupButton from './ElementExportPopupButton';
import MediaExportPopupButton from './MediaExportPopupButton';
import SettingsRow from '../../user/account/accountModal/components/SettingsRow';
import ToggleInput from '../../components/form/fields/ToggleInput';

// Selectors
import {
    currentBoardHasMediaElements,
    getCurrentBoardFileSizeSelector,
    getBoardExports,
    getCurrentBoardHasThirdPartyImagesSelector,
} from './exportSelector';

// Utils
import { getFileSizeForDisplay } from '../../../common/files/fileSizeUtils';

// Constants
import { MimeType } from '../../../common/media/mimeConstants';
import { BoardExportFormat, BoardExportQuality, BoardExportType } from '../../../common/export/exportConstants';
import { FILE_TYPES_INFO } from '../../../common/files/fileConstants';

// Styles
import './ExportPopup.scss';

const mapStateToProps = createStructuredSelector({
    totalFileSize: getCurrentBoardFileSizeSelector,
    currentBoardHasMediaElements: currentBoardHasMediaElements,
    boardExports: getBoardExports,
    currentBoardHasThirdPartyImages: getCurrentBoardHasThirdPartyImagesSelector,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type ExportPopupForBoardProps = PropsFromRedux & {
    elementId: string;
};

const ExportPopupForBoard = ({
    elementId,
    totalFileSize,
    currentBoardHasMediaElements,
    boardExports,
    currentBoardHasThirdPartyImages,
}: ExportPopupForBoardProps): ReactElement => {
    const fileSizeForDisplay = (totalFileSize > 0 && `(${getFileSizeForDisplay(totalFileSize)})`) || '';
    const [includeChildBoards, setIncludeChildBoards] = React.useState(false);

    const buttonTitle = currentBoardHasMediaElements
        ? `Generate ZIP file ${fileSizeForDisplay}`
        : `No files found on this board`;

    return (
        <div className="ExportPopup">
            <div className="export-section">
                <div className="export-options">
                    <h3>Export the current board layout</h3>
                    <ElementExportPopupButton
                        elementId={elementId}
                        mimeType={MimeType.PDF}
                        fileType={FILE_TYPES_INFO.PDF.type}
                        format={BoardExportFormat.canvas}
                        quality={BoardExportQuality.regular}
                    >
                        PDF standard (small file size)
                    </ElementExportPopupButton>
                    <ElementExportPopupButton
                        elementId={elementId}
                        mimeType={MimeType.PDF}
                        fileType={FILE_TYPES_INFO.PDF.type}
                        format={BoardExportFormat.canvas}
                        quality={BoardExportQuality.high}
                    >
                        PDF high quality (large file size)
                    </ElementExportPopupButton>
                    <ElementExportPopupButton
                        elementId={elementId}
                        mimeType={MimeType.PNG}
                        fileType={FILE_TYPES_INFO.IMAGE.type}
                        format={BoardExportFormat.canvas}
                    >
                        PNG image
                    </ElementExportPopupButton>
                </div>
            </div>
            <div className="divider" />
            <div className="export-section">
                <div className="export-options">
                    <h3>Convert to a linear document</h3>
                    <SettingsRow className="toggle-row" label="Include sub-boards">
                        <ToggleInput
                            active={includeChildBoards}
                            onChange={(event) => setIncludeChildBoards(event.target.checked)}
                        />
                    </SettingsRow>
                    <ElementExportPopupButton
                        elementId={elementId}
                        mimeType={MimeType.DOCX}
                        fileType={FILE_TYPES_INFO.DOCX.type}
                        format={BoardExportFormat.linear}
                        includeChildBoards={includeChildBoards}
                    >
                        Word document
                    </ElementExportPopupButton>
                    <ElementExportPopupButton
                        elementId={elementId}
                        mimeType={MimeType.MARKDOWN}
                        fileType={FILE_TYPES_INFO.TEXT.type}
                        format={BoardExportFormat.linear}
                        includeChildBoards={includeChildBoards}
                    >
                        Markdown
                    </ElementExportPopupButton>
                    <ElementExportPopupButton
                        elementId={elementId}
                        mimeType={MimeType.PLAIN_TEXT}
                        fileType={FILE_TYPES_INFO.TEXT.type}
                        format={BoardExportFormat.linear}
                        includeChildBoards={includeChildBoards}
                    >
                        Plain text
                    </ElementExportPopupButton>
                </div>
            </div>
            <>
                <div className="divider" />
                <div className="export-section">
                    <div className="export-options">
                        <h3>Download all images and files</h3>
                        <MediaExportPopupButton
                            elementId={elementId}
                            exportType={BoardExportType.BOARD_MEDIA}
                            totalFileSize={totalFileSize}
                            boardExports={boardExports}
                            disabled={!currentBoardHasMediaElements}
                            currentBoardHasThirdPartyImages={currentBoardHasThirdPartyImages}
                        >
                            {buttonTitle}
                        </MediaExportPopupButton>
                    </div>
                </div>
            </>
        </div>
    );
};

export default connector(ExportPopupForBoard);
